import PubNub, { CATEGORIES } from "pubnub";
import storage, { StorageKey } from "@/App/services/storage.service";
import userService from "@/Pages/Login/services/user.service";

export type LiveUpdateMessage<T> = {
  type: string;
  payload: T;
};

export interface SubscribeParams<T> {
  channels?: string[];
  types: string[];
  callback: (type: string, message: LiveUpdateMessage<T>) => void;
}

export interface PubnubService {
  subscribe<T>({ channels, types, callback }: SubscribeParams<T>): Promise<void>;
  unsubscribe(): void;
}

export default class PubnubServiceImpl implements PubnubService {
  private static instance?: PubnubService;

  static getInstance(): PubnubService {
    return PubnubServiceImpl.instance || (PubnubServiceImpl.instance = new PubnubServiceImpl());
  }

  private pubnubInstances: PubNub[] = [];

  subscribe<T>({ channels, types, callback }: SubscribeParams<T>): Promise<void> {
    const pubnubSubscriptionKey = storage.getItem(StorageKey.pubnubSubscriptionKey);
    if (!pubnubSubscriptionKey) {
      throw new Error("Pubnub subscription key not found");
    }
    const user = userService.getUser();
    if (!user) {
      throw new Error("User not found");
    }
    if (!channels?.length) return Promise.resolve();
    const pubnubInstance = new PubNub({
      subscribeKey: pubnubSubscriptionKey,
      userId: user.id,
    });
    this.pubnubInstances.push(pubnubInstance);
    pubnubInstance.subscribe({ channels });
    return new Promise<void>((resolve) => {
      pubnubInstance.addListener({
        message(pubnubMessage) {
          const message = pubnubMessage.message as LiveUpdateMessage<T> | undefined;
          if (!message) return;
          const messageType = message.type;
          if (types.includes(messageType)) {
            callback(messageType, message);
          }
        },
        status(statusEvent) {
          if (statusEvent.category === CATEGORIES.PNConnectedCategory) {
            resolve();
          }
        },
      });
    });
  }

  unsubscribe(): void {
    this.pubnubInstances.forEach((instance) => {
      instance.unsubscribeAll();
    });
    this.pubnubInstances = [];
  }
}
